import { Component, OnInit, OnDestroy, NgZone, ViewEncapsulation } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { timer, forkJoin } from 'rxjs';

import {
  DataService,
  QueueOverviewEndpointsService,
  GlobalVariables,
  QueueOverview,
  BranchEndpointService,
  BranchQueueOverview,
  SimpleToken,
  BranchQueueOverviewBooking,
} from '../common/index';
import { AuthButtonService } from '../auth-button/index';
import { NewAlertData, NewTokenData, PusherService } from 'app/common/_services/pusher.service';
import { calcWaitingTime, getMoodByWaitTime } from 'app/common/_utils/mood.util';
import { animate, style, transition, trigger } from '@angular/animations';
import { format } from 'date-fns';
import { underscoreToCamelCase } from '../common/_utils/string.util';

declare var swal: any;

export const tokenAnimation = trigger('tokenAnimation', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateX(-200px)' }),
    animate('400ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ opacity: 1, transform: 'translateX(0)' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, transform: 'translateX(0)', height: '*' }),
    animate('400ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ transform: 'translateX(25%)', opacity: 0 })),
    animate('400ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ height: 0 })),
  ]),
]);

@Component({
  selector: 'queue-overview',
  templateUrl: './queue-overview.component.html',
  styleUrls: ['./queue-overview.component.scss'],
  providers: [QueueOverviewEndpointsService],
  animations: [tokenAnimation],
  encapsulation: ViewEncapsulation.None, // remove once angular updated version fixes container queries not being scoped correctly
})
export class QueueOverviewComponent implements OnInit, OnDestroy {
  queueOverviewData;
  tokensLoaded = false;
  queues: BranchQueueOverview[] = [];
  dateToday = new Date();
  routeId;
  loggedIn;
  branchData;
  waitTimesSub;
  timer;
  mutedQueues = []; // list of id, of muted queues
  sound;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dataService: DataService,
    private authButtonService: AuthButtonService,
    private queueOverviewEndpointsService: QueueOverviewEndpointsService,
    private zone: NgZone,
    private sanitizer: DomSanitizer,
    private pusherService: PusherService,
    private branchService: BranchEndpointService
  ) {
    this.validateLogin();
    this.queueOverviewData = new QueueOverview();
  }

  ngOnInit() {
    if (this.loggedIn) {
      this.routeParams();
      this.loadBranch();
      this.initPusherSubscription();

      this.tokensLoaded = true;

      this.zone.run(() => {
        console.log('enabled page re-render');
      });
    }
  }

  ngOnDestroy() {
    this.queueOverviewData = null;
    this.queues = null;
    this.routeId = null;
    this.loggedIn = null;
    this.tokensLoaded = null;
    this.branchData = null;
    if (this.waitTimesSub) this.waitTimesSub.unsubscribe();
    this.waitTimesSub = null;
    clearInterval(this.timer);
    this.timer = null;
    this.dateToday = null;
  }

  initPusherSubscription() {
    const branchId = this.route.snapshot.params['branchId'];
    this.pusherService.connectTokenBroadcast(branchId, this.handleNewTokenData, this.handleNewAlertData);
  }

  handleNewTokenData = (data: NewTokenData) => {
    const { id, attributes } = data.data;
    const { branchQueueId, createdAt, status, tokenNumber, originType, totalPeopleInQueue, bookingId } = attributes;

    const branchQueue = this.queues.find((it) => it.branchQueueId === branchQueueId);
    if (!branchQueue) return;

    const { mood } = branchQueue;
    const queueIsMuted = this.mutedQueues.includes(branchQueue.id);

    if (status === 'pending') {
      const waitTime = Math.floor(calcWaitingTime(createdAt) / 1000);

      const newToken: SimpleToken = {
        id,
        createdAt,
        originType,
        status,
        tokenNumber,
        waitTime,
        mood: getMoodByWaitTime(waitTime, mood),
        tokenTags: [],
        branchQueueId,
      };

      if (bookingId) {
        const booking = branchQueue.bookings.find((it) => `${it.id}` === `${bookingId}`);
        if (booking) booking.tokenNumber = tokenNumber;
      }

      if (!queueIsMuted && totalPeopleInQueue === 1) {
        this.branchService.playTokenEventSound(this.branchService.soundEffects.newToken);
      }

      branchQueue.tokens.push(newToken);
    } else if (['no_show', 'served', 'serving'].includes(status)) {
      const index = branchQueue.tokens.map((it) => it.id).indexOf(id);

      if (index > -1) {
        branchQueue.tokens.splice(index, 1);
      }
    }
  };

  handleNewAlertData = (data: NewAlertData) => {
    if ('booking_date' in data) {
      const { branch_queue_id } = data;
      const queue = this.queues.find((q) => `${q.branchQueueId}` === `${branch_queue_id}`);

      const formattedData = underscoreToCamelCase(data);
      const existingBooking = queue?.bookings.find((b) => `${b.id}` === `${data.id}`);

      if (existingBooking) {
        existingBooking.tokenNumber = formattedData.tokenNumber;
        existingBooking.status = formattedData.status;
        return;
      }

      queue?.bookings.push({
        ...formattedData,
        tokenId: data.token?.id,
        bookingDate: format(new Date(formattedData.bookingDate), 'HH:mm'),
      });
    }
  };

  validateLogin() {
    this.loggedIn = this.dataService.sessionData();

    if (!this.loggedIn) {
      this.router.navigate(['/login']);
    }

    this.authButtonService.login();
  }

  routeParams() {
    this.route.params.subscribe((params) => {
      this.routeId = +params['id']; // (+) converts string 'id' to a number
    });
  }

  loadBranch() {
    this.queueOverviewEndpointsService.getBranchData().subscribe({
      next: (data: any) => {
        this.branchData = data.data;
        this.loadQueueOverview();
      },
      error: (error) => {
        swal({
          text: this.errorMessage(error),
          type: 'warning',
          allowOutsideClick: false,
          showCancelButton: false,
          allowEscapeKey: false,
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Refresh the page!',
        }).then(() => {
          if (error && error[0].status == '401') {
            this.dataService.clearData();
            this.router.navigate(['login']);
          } else {
            location.reload();
          }
        });
      }
    });
  }

  loadQueueOverview() {
    forkJoin({
      overviewRes: this.queueOverviewEndpointsService.getQueueOverviewData(this.routeId),
      integrationsRes: this.branchService.getBranchQueueIntegrations({}),
      bookingsRes: this.branchService.getBookings({}),
    }).subscribe({
      next: ({ overviewRes, integrationsRes = { data: [] }, bookingsRes }) => {
        this.queueOverviewData = new QueueOverview();
        this.queueOverviewData.mood.setMoodData(this.branchData);
        this.queueOverviewData.setQueueOverviewData(overviewRes.data);
        this.queues = this.queueOverviewData.queues;

        const integrationsByQueue: Record<string, string> =
          integrationsRes?.data?.reduce((acc, it) => {
            acc[`${it.attributes?.branchQueueId}`] = it.attributes?.type || '';
            return acc;
          }, {}) || {};

        const bookingsByQueue: Record<string, BranchQueueOverviewBooking[]> = {};
        bookingsRes?.data?.forEach((booking) => {
          const key = `${booking.attributes.branchQueueId}`;
          (bookingsByQueue[key] ||= []).push({
            id: booking.id,
            ...booking.attributes,
            tokenId: booking.attributes.tokenId,
            tokenNumber: booking.attributes.tokenNumber,
            bookingDate: format(new Date(booking.attributes.bookingDate), 'HH:mm'),
          });
        });

        this.queues.forEach((q) => {
          const key = `${q.branchQueueId}`;
          q.integrationType = integrationsByQueue[key] || '';

          if (q.integrationType === 'helseboka_booking') {
            q.bookings = bookingsByQueue[key] || [];
          }
        });

        this.mutedQueues = [];
        this.muteQueuesFromURL();
        this.waitingTimesForToken();
      },
      error: (error) => {
        console.log(error);
        swal({
          text: this.errorMessage(error),
          type: 'warning',
          allowOutsideClick: false,
          showCancelButton: false,
          allowEscapeKey: false,
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Refresh the page!',
        }).then(() => {
          console.log(error);
          if (error && error[0].status == '401') {
            this.dataService.clearData();
            this.router.navigate(['login']);
          } else {
            location.reload();
          }
        });
      }
    });
  }

  waitingTimesForToken(): void {
    this.timer = timer(0, 1000 * 60);

    this.waitTimesSub = this.timer.subscribe(() => {
      this.queues.forEach((queue, index, arr) => {
        queue.tokens.forEach((token) => {
          token.waitTime += 60;
          token.mood = getMoodByWaitTime(token.waitTime, queue.mood);
        });
      });
    });
  }

  muteQueue(queueId: string): void {
    //if no mute params in URL:
    //catch if undefined to preserve "?mute=" default param and not add it to mutedqueues
    if (queueId !== 'undefined') {
      const splicedQueue = this.mutedQueues.filter((id) => id !== queueId); // remove queueId

      if (splicedQueue.length !== this.mutedQueues.length) {
        this.mutedQueues = splicedQueue;
      } else this.mutedQueues.push(queueId);
    }

    /* updates URL query when mute button gets clicked
     TODO fix %2C in url when updating
     the commented code below should fix it, but it doesn't work, will look at it further:
     let muteParams: string = decodeURIComponent(this.mutedQueues.join(",")) */
    this.router.navigate([], {
      queryParams: {
        mute: this.mutedQueues,
      },
    });
  }

  muteQueuesFromURL() {
    //cast mutedParam to string, if not it will be undefined.
    //TODO might be a better way to get query params
    const mutedParam = String(this.route.snapshot.queryParams['mute']);
    const mutedArr = mutedParam.split(',');
    mutedArr.forEach((queueID) => this.muteQueue(queueID));
  }

  errorMessage(error) {
    let message;

    if (error[0] && error[0].status == '404') {
      message = GlobalVariables.ERROR_MESSAGE_404;
    } else if (error[0] && error[0].message) {
      message = error[0].message;
    } else {
      message = GlobalVariables.DEFAULT_ERROR_MESSAGE;
    }

    return message;
  }
}
